import type { UserSettingsState } from "./user-settings";

/** Built-in suggestions; users can hide any from quick-pick or add their own via typing. */
export const BASIC_CATEGORIES = [
  "General",
  "Software",
  "Cloud",
  "Productivity",
  "Media",
  "Gaming",
  "Fitness",
  "Finance",
  "News",
  "Other",
] as const;

export type BasicCategory = (typeof BASIC_CATEGORIES)[number];

const BASIC_LOWER = new Set(
  BASIC_CATEGORIES.map((b) => b.toLowerCase()),
);

export function normalizeCategoryLabel(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

export function canonicalBasicName(raw: string): BasicCategory | null {
  const n = normalizeCategoryLabel(raw).toLowerCase();
  const i = BASIC_CATEGORIES.findIndex((b) => b.toLowerCase() === n);
  return i >= 0 ? BASIC_CATEGORIES[i]! : null;
}

export function isBasicCategoryName(raw: string): boolean {
  return BASIC_LOWER.has(normalizeCategoryLabel(raw).toLowerCase());
}

export function addSavedCategory(prev: string[], raw: string): string[] {
  const n = normalizeCategoryLabel(raw);
  if (!n) return prev;
  const store = canonicalBasicName(n) ?? n;
  const lower = store.toLowerCase();
  if (prev.some((x) => x.toLowerCase() === lower)) return prev;
  return [...prev, store];
}

/** Chips + datalist: built-ins (unless dismissed), then saved customs in order. */
export function orderedCategorySuggestions(
  settings: Pick<
    UserSettingsState,
    "savedCategoryNames" | "dismissedBasicCategories"
  >,
): string[] {
  const dismissed = new Set(
    settings.dismissedBasicCategories.map((x) => normalizeCategoryLabel(x).toLowerCase()),
  );
  const out: string[] = [];
  for (const b of BASIC_CATEGORIES) {
    if (!dismissed.has(b.toLowerCase())) out.push(b);
  }
  for (const s of settings.savedCategoryNames) {
    const n = normalizeCategoryLabel(s);
    if (!n) continue;
    if (out.some((x) => x.toLowerCase() === n.toLowerCase())) continue;
    out.push(n);
  }
  return out;
}

export function datalistOptions(
  settings: Pick<
    UserSettingsState,
    "savedCategoryNames" | "dismissedBasicCategories"
  >,
  currentValue: string,
): string[] {
  const base = orderedCategorySuggestions(settings);
  const cur = normalizeCategoryLabel(currentValue);
  if (cur && !base.some((x) => x.toLowerCase() === cur.toLowerCase())) {
    return [cur, ...base];
  }
  return base;
}
